/**
 * The portal contract every platform API serves at GET v3/{platform}/admin/capabilities
 * (docs/architecture/admin-portal-shape.md §5). The portal builds its menus from it; the API
 * still refuses anything the caller may not do.
 */

export type SectionId = 'find' | 'operations' | 'monitoring' | 'configuration' | 'access' | 'tools';

export type ConfirmLevel = 'none' | 'reason' | 'typed';

export interface CapabilitySection {
  id: SectionId | string;
  label: string;
}

export interface CapabilityAction {
  id: string;
  section: SectionId | string;
  method: string;
  route: string;
  label: string;
  confirm: ConfirmLevel | string;
}

export interface Capabilities {
  success?: boolean;
  platform: string;
  displayName?: string;
  apiVersion?: string;
  isAdmin: boolean;
  sections: CapabilitySection[];
  actions: CapabilityAction[];
}

export type PlatformId = 'retail' | 'hub' | 'loans' | 'finance';

/** Where a platform stands for the signed-in person. */
export type PlatformStatus =
  | { kind: 'loading' }
  | { kind: 'ready'; caps: Capabilities; source: 'api' | 'roles' }
  | { kind: 'consent-waiting' }
  | { kind: 'not-admin' }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string };

/** The six sections, in the order every platform shows them. */
export const SIX_SECTIONS: ReadonlyArray<{ id: SectionId; label: string; holds: string }> = [
  { id: 'find', label: 'Find', holds: 'Look up a record by id or name.' },
  { id: 'operations', label: 'Operations', holds: 'Queues and actions that keep work moving: resend, retry, reconcile.' },
  { id: 'monitoring', label: 'Monitoring', holds: 'Jobs, detectors, health, support reports and audits.' },
  { id: 'configuration', label: 'Configuration', holds: 'Settings and reference data, stored in SQL. Every save is audited.' },
  { id: 'access', label: 'Access and roles', holds: 'Who can do what.' },
  { id: 'tools', label: 'Tools', holds: 'Read-only diagnostics.' },
];
